import styles from 'components/Ad/styles.scss';
import Splash from 'components/Shared/Splash';
import Brand from 'components/Shared/Brand';
import OnTvBadge from 'components/Shared/OnTvBadge';
import Image from 'components/Shared/Image';
import Button from 'components/Shared/Button';
import { IconArrow, IconCheckmark } from 'components/Shared/Icon';
import cn from 'classnames';
import { Block } from 'components/Shared/Layout/';
import { NotifyLink } from 'components/Shared/NotifyLink';
import { AdTemplate } from 'models/AdTemplate';
import { Ad } from 'models/Ad';
import { SliderAd as SliderAdModel } from 'models/SliderAd';
import { ImageAd as ImageAdModel } from 'models/ImageAd';
import {
  MergedProps,
  ImageAdProps,
  ComposedAdProps,
  getCreative,
  getImage,
  ImageAdType,
  ComposedAdType,
  getSlot
} from 'components/Ad';
import { pictureDimensions } from './pictureDimensions';
import { CardLarge } from 'components/Card';
import { useIntersectionObserver } from 'hooks';
import { TrackedElement } from 'hooks/useIntersectionObserver';
import { GtmPromotion } from 'store/googleTagManager/types';

export const shouldRenderUsps = (appearanceType: ComposedAdType, ad: Ad): ad is SliderAdModel =>
  appearanceType === AdTemplate.PanoramaTwoThirds && (ad as SliderAdModel).usps !== undefined;

export const createImageAd =
  (appearanceType: ImageAdType) =>
  ({ position = 1, ad, className, onAdView, onAdClick }: ImageAdProps & MergedProps) => {
    const { googleAnalyticsName, googleAnalyticsKey } = ad;
    const image = getImage(ad);

    const { setElementRef } = useIntersectionObserver({
      onVisible: handleElementVisible
    });

    function handleElementVisible(element: TrackedElement<GtmPromotion>) {
      onAdView(element.payload);
    }

    const gtmPromotion = {
      promotion_id: googleAnalyticsKey,
      promotion_name: googleAnalyticsName,
      creative_name: getCreative(appearanceType),
      creative_slot: getSlot(position)
    };

    const handleOnClick = () => {
      onAdClick(gtmPromotion);
    };

    const adContent = ad.url ? (
      <NotifyLink
        ticket={ad.ticket}
        url={ad.url}
        className={styles.imageAdWrapper}
        onClick={handleOnClick}
      >
        <CardLarge
          button={{ title: (ad as ImageAdModel).buttonCaption }}
          information={{
            title: ad.title,
            text: (ad as ImageAdModel).subTitle ?? ''
          }}
          appearance="card"
          image={image}
        />
      </NotifyLink>
    ) : (
      <div className={styles.imageAdWrapper}>
        <CardLarge
          button={{ title: (ad as ImageAdModel).buttonCaption }}
          information={{
            title: ad.title,
            text: (ad as ImageAdModel).subTitle ?? ''
          }}
          appearance="card"
          image={image}
        />
      </div>
    );

    return (
      <Block
        ref={node => setElementRef({ ref: node, payload: gtmPromotion })}
        className={cn(styles.imageAd, styles.base, className)}
      >
        {adContent}
      </Block>
    );
  };

export const createComposedAd =
  (appearanceType: ComposedAdType) =>
  ({
    ad,
    gotoOfferText,
    className,
    shadow = false,
    position = 1,
    appearance = 'normal',
    onAdView,
    onAdClick
  }: ComposedAdProps & MergedProps) => {
    const { googleAnalyticsKey, googleAnalyticsName } = ad;
    const dimension = pictureDimensions(appearanceType);

    const { setElementRef } = useIntersectionObserver({
      onVisible: handleElementVisible
    });

    function handleElementVisible(element: TrackedElement<GtmPromotion>) {
      onAdView(element.payload);
    }

    const gtmPromotion = {
      promotion_id: googleAnalyticsKey,
      promotion_name: googleAnalyticsName,
      creative_name: getCreative(appearanceType),
      creative_slot: getSlot(position)
    };

    const handleOnClick = () => {
      onAdClick(gtmPromotion);
    };

    return (
      <Block
        ref={node => setElementRef({ ref: node, payload: gtmPromotion })}
        shadow={(shadow && ((appearanceType === AdTemplate.Medium && 'three') || 'two')) || false}
        className={cn(
          styles.base,
          {
            [styles.mediumAd]: appearanceType === AdTemplate.Medium,
            [styles.panoramaAd]: appearanceType === AdTemplate.PanoramaTwoThirds,
            [styles.slimmed]: appearance === 'slimmed'
          },
          className
        )}
      >
        <NotifyLink ticket={ad.ticket} url={ad.url} onClick={handleOnClick}>
          <Splash
            splashTextTop={ad.splashTextTop}
            splashTextMiddle={ad.splashTextMiddle}
            splashTextBottom={ad.splashTextBottom}
            splashTextEnd={ad.splashTextEnd}
            appearance={appearance === 'slimmed' ? 'small' : undefined}
            className={styles.splash}
          />
          {ad.brandImage && (
            <div className={styles.brandContainer}>
              <Brand
                appearance={appearance === 'slimmed' ? 'small' : 'medium'}
                {...ad.brandImage}
              />
            </div>
          )}
          <div className={styles.imageWrapper}>
            {ad.isOnTv && <OnTvBadge className={styles.onTvBadge} />}
            <Image
              className={styles.adImage}
              paddingBottom
              {...ad.adImage}
              {...dimension}
              alt={ad.title}
            />
          </div>
          <div className={styles.adInfo}>
            <div className={styles.adInfoUpper}>
              <h5 className={styles.preTitle}>{ad.preTitle}</h5>
              <h4 className={styles.title}>{ad.title}</h4>
            </div>
            <div className={styles.adInfoLower}>
              <p className={styles.description}>{ad.description}</p>
              {shouldRenderUsps(appearanceType, ad) && (
                <ul className={styles.usps}>
                  {ad.usps.map(usp => (
                    <li key={usp}>
                      <IconCheckmark size="small" />
                      {usp}
                    </li>
                  ))}
                </ul>
              )}
              <Button className={styles.adButton}>
                {gotoOfferText}
                <IconArrow size="small" className={styles.adButtonArrow} />
              </Button>
            </div>
          </div>
        </NotifyLink>
      </Block>
    );
  };
